import axios from "axios";
import { translateMenuText } from "./menuTranslation";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import i18n from "./i18n";
import { getStoredLanguage, setStoredLanguage } from "./languageStorage";

const LocalizationContext = createContext(null);

// IMPORTANT: Language selection should NOT change UI direction/alignment.
// We keep the app direction LTR regardless of chosen language.
const getDir = (_lang) => "ltr";

const applyDocumentLangDir = (lang) => {
    if (typeof document === "undefined") return;
    const dir = getDir(lang);
    try {
        document.documentElement.lang = lang;
        document.documentElement.dir = dir;
        document.body.dir = dir;
    } catch {
        // ignore
    }
};

export function LocalizationProvider({ children }) {
    const [language, setLanguageState] = useState(() => getStoredLanguage());

    const [menuTranslations, setMenuTranslations] = useState({});
    useEffect(() => {
        if (language !== "ar") return undefined;
        let active = true;
        const refresh = () => axios.get("method/ui_builder.api.get_merged_translations", {
            params: { language: "ar" },
        }).then(({ data }) => {
            if (active) setMenuTranslations(data?.message?.translations || {});
        }).catch(() => {
            // Keep original menu text usable when the translation service is unavailable.
        });
        refresh();
        window.addEventListener("focus", refresh);
        return () => { active = false; window.removeEventListener("focus", refresh); };
    }, [language]);
    const menuText = useCallback((text) => translateMenuText(text, language, menuTranslations), [language, menuTranslations]);

    // Ensure DOM + i18n match initial language immediately.
    useMemo(() => {
        applyDocumentLangDir(language);
        if (i18n.language !== language) {
            i18n.changeLanguage(language);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setLanguage = useCallback((nextLang) => {
        if (!nextLang || nextLang === language) return;
        setLanguageState(nextLang);
        setStoredLanguage(nextLang);
        applyDocumentLangDir(nextLang);
        i18n.changeLanguage(nextLang);
    }, [language]);

    const value = useMemo(() => {
        return {
            language,
            menuText,
            dir: getDir(language),
            setLanguage,
            isRTL: getDir(language) === "rtl",
        };
    }, [language, setLanguage, menuText]);

    return (
        <LocalizationContext.Provider value={value}>
            {children}
        </LocalizationContext.Provider>
    );
}

export function useLocalization() {
    const ctx = useContext(LocalizationContext);
    if (!ctx) {
        throw new Error("useLocalization must be used within LocalizationProvider");
    }
    return ctx;
}

